import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, Button, Badge } from 'react-bootstrap';
import SearchBar from '../../components/SearchBar/SearchBar';
import Reload from '../../components/reload/reload';
import Headers from '../../components/Headers/Headers';
import Page from '../../components/Page/Page';
import ModernTable from '../../components/ModernTable/ModernTable';
import { useNavigate } from 'react-router-dom';
import dateFormat from 'dateformat';
import useUnits from '../../hooks/useUnits';

import decodeJwt from '../../hooks/decode';

const Units = () => {
  const navigate = useNavigate();
  
  const userDecoded = decodeJwt();
  const { permissionSGU } = userDecoded;
  if (permissionSGU?.units?.read !== true) {
    navigate("/home");
  }

  const data = useUnits();

  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState(data);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const handleSearchData = useCallback((value) => {
    const searchValue = value.toUpperCase();
    const filtered = data.filter(item =>
      (item?.name?.toUpperCase().includes(searchValue)) ||
      (item?.email?.toUpperCase().includes(searchValue)) ||
      (item?.cnpj?.toUpperCase().includes(searchValue))
    );
    setFilteredData(filtered);
    setPage(0);
  }, [data]);

  const totalPages = useMemo(() => Math.ceil(filteredData.length / rowsPerPage), [filteredData.length, rowsPerPage]);
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handleChangePage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0); // Reset to first page when changing rows per page
  }, []);

  const getPaginationRange = useCallback(() => {
    const start = Math.max(page - 2, 0);
    const end = Math.min(page + 2, totalPages - 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [page, totalPages]);

  const handlePage = useCallback((link) => {
    navigate(link);
  }, [navigate]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 200);
    return () => clearTimeout(timeout);
  }, []);


  const columns = ["UNIDADE", "CNPJ", "EQUIPAMENTOS", "ÚLTIMO ACESSO", "AÇÕES"];

  // Função para renderizar os botões de ação
  const renderActions = (item, index) => (
    <div className="d-flex justify-content-center gap-2">
      <Button
        size="sm"
        variant="outline-primary"
        className="px-3"
        onClick={() => handlePage(`/units/membros/${item[4]}`)}
        disabled={!item[4] || userDecoded?.hideSensitiveInfo}
        style={{
          borderRadius: '8px',
          transition: 'all 0.2s ease'
        }}
      >
        Membros
      </Button>
      <Button
        size="sm"
        variant="outline-success"
        className="px-3"
        onClick={() => handlePage(`/units/details/${item[4]}`)}
        disabled={!item[4]}
        style={{
          borderRadius: '8px',
          transition: 'all 0.2s ease'
        }}
      >
        Visualizar
      </Button>
    </div>
  );

  // Configuração da paginação para ModernTable
  const paginationConfig = {
    currentPage: page,
    totalPages: totalPages,
    rowsPerPage: rowsPerPage,
    total: filteredData.length,
    currentStart: page * rowsPerPage,
    currentEnd: (page + 1) * rowsPerPage,
    pageNumbers: getPaginationRange(),
    onChangePage: handleChangePage,
    onChangeRowsPerPage: handleChangeRowsPerPage
  };

  return (
    <Page title="Unidades">
      <Headers page="Unidades" />

      {loading && <Reload />}
      
      <SearchBar onSearch={handleSearchData} />

      <Card className="border-0 shadow-sm mt-2">
        <Card.Header className="bg-white border-0 py-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0 fw-semibold text-white">Lista de Unidades</h5>
              <small className="text-white">
                Mostrando {paginatedData.length} de {filteredData.length} unidades
              </small>
            </div>
            <Badge bg="light" text="dark" className="px-3 py-2">
              <i className="fas fa-list me-2"></i>
              {filteredData.length} registros
            </Badge>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          <ModernTable
            columns={columns}
            data={paginatedData.map((item) => ([
              userDecoded?.hideSensitiveInfo ? '***' : item.name,
              userDecoded?.hideSensitiveInfo ? '***' : item.cnpj,
              item.equipments.length,
              item.updatedAt ? dateFormat(item.updatedAt, "dd/mm/yyyy HH:MM:ss") : '--',
              item._id
            ]))}
            actions={renderActions}
            pagination={paginationConfig}
          />
        </Card.Body>
      </Card>
    </Page>
  );
};

export default Units;
